import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import styled from '@emotion/styled';
import DevicesView from '../components/DevicesView';
import { Device } from '../models/device';

// While this screen is open we re-fetch the whole fleet every 10s. Users sit
// here plugging devices in and watch them come online, so we poll continuously
// rather than only on demand: newly connected devices appear, status changes
// land, and a device the backend auto-pings on first connect shows up as
// `pinging` (button disabled) with no user action. The same loop also settles
// a manual ping. See device-data-backend.md.
const POLL_INTERVAL_MS = 10000;

const DEVICE_FIELDS = `
  id
  serial
  status
  pinging
  lastHeartbeatLabel
  locationName
  cameras {
    id
    name
    status
    imageUrl
    errorKind
  }
`;

const GET_DEVICES_QUERY = gql`
  query GetDevices {
    devices {
      ${DEVICE_FIELDS}
    }
  }
`;

const PING_DEVICE_MUTATION = gql`
  mutation PingDevice($id: ID!) {
    pingDevice(id: $id) {
      ${DEVICE_FIELDS}
    }
  }
`;

// Capability-gated Devices section. Access is gated by the MANAGE_DEVICES
// capability via PrivateRoute (see App.tsx); the API independently enforces
// that capability server-side (see device-data-backend.md).
export default () => {
  const { data, loading, error } = useQuery(GET_DEVICES_QUERY, {
    pollInterval: POLL_INTERVAL_MS,
  });
  const [pingDeviceMutation] = useMutation(PING_DEVICE_MUTATION);
  // Optimistic in-flight set: covers the gap between the click and the
  // mutation result landing in the cache. Once the backend `pinging` flag comes
  // back true it takes over as the source of truth (and the poll clears it).
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const onPing = (device: Device) => {
    // Button is disabled while pinging, but guard against a double-trigger.
    if (device.pinging || pending[device.id]) return;
    setPending((prev) => ({ ...prev, [device.id]: true }));
    // The mutation returns the device (pinging: true) with the same id, so
    // Apollo merges it into the normalized cache and the card updates itself.
    pingDeviceMutation({ variables: { id: device.id } })
      .catch(() => {
        /* transient failures are picked up by the next poll; don't crash the click */
      })
      .finally(() =>
        setPending((prev) => {
          const next = { ...prev };
          delete next[device.id];
          return next;
        })
      );
  };

  const devices: Device[] | undefined = data?.devices;

  // Only block on the very first load / a hard error; keep showing data through
  // background poll refetches and transient poll errors.
  if (!devices && (loading || error)) {
    return (
      <StyledLoading>
        <h1>Devices</h1>
        <p>{error ? `Couldn’t load devices: ${error.message}` : 'Loading devices…'}</p>
      </StyledLoading>
    );
  }

  const list = devices || [];

  // The view's `pinging` map is the backend `pinging` flag (single source of
  // truth, so auto-pings show too) plus any locally-pending clicks.
  const pinging: Record<string, boolean> = { ...pending };
  list.forEach((d) => {
    if (d.pinging) pinging[d.id] = true;
  });

  return <DevicesView devices={list} pinging={pinging} onPing={onPing} />;
};

const StyledLoading = styled.div`
  margin: 44px 52px 0;
  color: #595959;
  font-family: Helvetica, sans-serif;
`;
